import { Link } from "@tanstack/react-router";
import { BrandLogo } from "@/components/BrandLogo";

export type ChannelConnectedProvider = "slack" | "teams";

const PROVIDER_NAMES: Record<ChannelConnectedProvider, string> = { slack: "Slack", teams: "Microsoft Teams" };

/**
 * Where a Slack or Teams install lands outside the app. A web install returns
 * to settings (`/o/:orgId/settings?slack=…`); this page is the native one's
 * return, `/{provider}/connected?{provider}=verify&orgId=…&flow=…`, which is a
 * universal link into the Rivets app. When the app is not installed on this
 * device, or the link was opened somewhere that does not hand off, the
 * browser shows this instead of a 404. The `flow` is never redeemed here: only
 * the app that started the install holds its verifier.
 */
export function channelConnectedMessage(provider: ChannelConnectedProvider, result: string | null): {
  title: string; body: string; error?: boolean;
} {
  const name = PROVIDER_NAMES[provider];
  switch (result) {
    case "verify":
      return {
        title: "Return to the Rivets app",
        body: `Open the Rivets app on this device to finish connecting ${name}. The app confirms it started this installation before anything is saved. If the app didn't open, start the installation again from the app's settings.`,
      };
    case "connected":
      return { title: `${name} connected`, body: `Rivets can now answer messages in ${name} for this organization.` };
    case "error-claimed":
      return { title: `${name} workspace already connected`, body: `That ${name} workspace belongs to another Rivets organization.`, error: true };
    case "error-tenant-changed":
      return {
        title: "A different workspace is connected",
        body: `This organization is already connected to another ${name} workspace. Uninstall that one in settings first.`,
        error: true,
      };
    case "error-binding":
      return { title: "Connection not completed", body: "This installation was started somewhere else. Start it again from Rivets settings.", error: true };
    default:
      return { title: `${name} connection failed`, body: `Rivets could not complete the ${name} installation. Return to settings and try again.`, error: true };
  }
}

export function ChannelConnectedPage({ provider }: { provider: ChannelConnectedProvider }) {
  const params = new URLSearchParams(window.location.search);
  const message = channelConnectedMessage(provider, params.get(provider));
  const orgId = params.get("orgId");
  return (
    <main className="auth-shell">
      <section className="auth-card" aria-live="polite">
        <BrandLogo className="auth-logo" size={22} />
        <div className="panel" style={{ textAlign: "center", padding: 24 }}>
          <div
            aria-hidden="true"
            style={{
              width: 38, height: 38, margin: "0 auto 16px", borderRadius: "50%",
              display: "grid", placeItems: "center",
              border: `1px solid ${message.error ? "var(--danger)" : "var(--success)"}`,
              color: message.error ? "var(--danger)" : "var(--success)",
              fontSize: "var(--glyph-xl)",
            }}
          >
            {message.error ? "!" : "✓"}
          </div>
          <h1 className="auth-title" style={{ marginBottom: 8 }}>{message.title}</h1>
          <p className="auth-subtitle" style={{ margin: "0 auto 20px", maxWidth: 360 }}>{message.body}</p>
          {orgId
            ? <a className="btn btn-primary btn-block" href={`/o/${encodeURIComponent(orgId)}/settings#integrations`}>Open Rivets settings</a>
            : <Link className="btn btn-primary btn-block" to="/">Open Rivets</Link>}
        </div>
      </section>
    </main>
  );
}

export function SlackConnectedPage() { return <ChannelConnectedPage provider="slack" />; }
export function TeamsConnectedPage() { return <ChannelConnectedPage provider="teams" />; }
